import { IAddressInputAddress } from '@opendoor/bricks';
import {
  accept,
  base,
  body,
  createFetch,
  createStack,
  header,
  init,
  json,
  method,
  params,
} from 'http-client';
import * as Cookies from 'js-cookie';
import moment from 'moment';
import queryString from 'query-string';

import { RepairPreference } from '../declarations';
import { IListingOfferParams } from '../declarations/agentResaleOfferPortal';
import { CONSUMER_BFF_URL, DEPLOY_TYPE, RAILS_URL } from '../helpers/globals';

export const COOKIE_KEY = DEPLOY_TYPE ? 'XSRF-G-TOKEN' : 'XSRF-TOKEN';
const BASE_URL = '/api/v1';

class ResponseError extends Error {
  public response: Response;

  constructor(message, response) {
    super(message);
    this.response = response;
  }
}

// N.B. this is a function because `Cookies.get` can change
// during runtime, we don't want to use the original (or nonexistant)
// one from JS-load time
const commonStack = () =>
  createStack(
    init('credentials', 'include'),
    accept('application/json'),
    header('Content-Type', 'application/json'),
    header('X-CSRF-Token', Cookies.get(COOKIE_KEY)),
    base(RAILS_URL),
  );

function doGet(url: string): Promise<Response> {
  return createFetch(commonStack())(url);
}

function doPost(url: string, jsonParams: object): Promise<Response> {
  const stack = createStack(commonStack(), method('POST'), json(jsonParams));
  return createFetch(stack)(url);
}

function doApiGet(url: string, getParams?: object): Promise<Response> {
  let stack = createStack(commonStack());
  if (getParams) {
    stack = createStack(stack, params(getParams));
  }
  return createFetch(stack)(`${BASE_URL}${url}`);
}

function doApiPost(url: string, jsonParams: object): Promise<Response> {
  return doPost(`${BASE_URL}${url}`, jsonParams);
}

export function doBFFPost(jsonParams: object): Promise<Response> {
  const apiUrl = `${CONSUMER_BFF_URL}/api`;
  const commonStack = createStack(
    init('credentials', 'include'),
    accept('application/json'),
    header('Content-Type', 'application/json'),
    header('X-CSRF-Token', Cookies.get(COOKIE_KEY)),
  );
  const stack = createStack(commonStack, method('POST'), json(jsonParams));
  return createFetch(stack)(apiUrl);
}

function doApiPatch(url: string, jsonParams: object): Promise<Response> {
  return createFetch(commonStack(), method('PATCH'), json(jsonParams))(`${BASE_URL}${url}`);
}

function doApiDelete(url: string, jsonParams: object): Promise<Response> {
  return createFetch(commonStack(), method('DELETE'), json(jsonParams))(`${BASE_URL}${url}`);
}

function handleResponse(res) {
  if (res.ok) {
    return res.json();
  }

  throw new ResponseError(`${res.status} ${res.statusText}`, res);
}

// Handle HEAD type responses, handleResponse (res.json) will throw
// if it's a HEAD 200 response.
function handleHeadResponse(res) {
  if (res.ok) {
    return res;
  }
  throw new ResponseError(`${res.status} ${res.statusText}`, res);
}

export function doFileUpload(url: string, file: File): Promise<Response> {
  return createFetch(method('PUT'), body(file))(url);
}

export function fetchHomeProfile(homeProfileSlug: string, leadToken?: string) {
  let endpoint = `/home_profile/data/${homeProfileSlug}`;
  if (leadToken) {
    endpoint = `${endpoint}/${leadToken}`;
  }
  return doApiGet(endpoint).then(res => res.json());
}

export function claimListingOfferByToken(token: string) {
  return doApiPost('/listing_offers/claim_listing_offer_by_token', {
    listing_offer_token: token,
  }).then(res => res.json());
}

export function fetchListingOffer(listingOfferId: string) {
  return doApiGet(`/listing_offers/${listingOfferId}`).then(handleResponse);
}

export function fetchListingOffers() {
  return doApiGet(`/listing_offers/dashboard_listing_offers`).then(handleResponse);
}

export function fetchChecklistDocumentUrl(documentId: number, listingOfferId: number) {
  return doApiGet(`/listing_offers/${listingOfferId}/checklist_document`, {
    document_id: documentId,
  }).then(handleResponse);
}

export function fetchOpendoorEstimate(address: IAddressInputAddress) {
  return doApiGet(`/home-value-estimate/valuation-cents`, {
    postal_code: address.postal_code,
    state: address.state,
    street1: address.street1,
  }).then(handleResponse);
}

export function postChecklistDocument(
  name: string,
  documentType: string,
  fileBase64DataUrl: string,
  listingOfferId: number,
  checklistItemId?: number,
) {
  return doApiPost(`/listing_offers/${listingOfferId}/upload_checklist_document`, {
    checklist_item_id: checklistItemId,
    document_type: documentType,
    file: fileBase64DataUrl,
    name,
  }).then(handleResponse);
}

export function deleteChecklistDocument(documentId: number, listingOfferId: number) {
  return doApiDelete(`/listing_offers/${listingOfferId}/remove_checklist_document`, {
    document_id: documentId,
  }).then(handleResponse);
}

export function requestClosingDateChange(
  listingOfferId: number,
  newDate: moment.Moment,
  reason: string,
) {
  return doApiPatch(`/listing_offers/${listingOfferId}/request_coe_date_change`, {
    new_date: newDate,
    reason,
  }).then(handleResponse);
}

export function postBuyerRepairPreferences(
  listingOfferId: number,
  notes: string | null,
  preferredSellerResponse: RepairPreference,
) {
  return doApiPost(`/listing_offers/${listingOfferId}/update_buyer_repair_preference`, {
    notes,
    preferred_seller_response: preferredSellerResponse,
  }).then(handleResponse);
}

export function postGenerateSellerResponse(listingOfferId: number) {
  return doApiPost(`/listing_offers/${listingOfferId}/generate_seller_repair_response`, {}).then(
    handleResponse,
  );
}

export function* fetchMarketTrends(market: string, zipCode: string) {
  const response = yield doApiGet('/home_profile/market_trends', { market, zip_code: zipCode });
  const { market_prices: marketPrices, zip_code_prices: zipCodePrices } = yield response.json();
  return { marketPrices, zipCodePrices };
}

export function fetchFlow(leadToken: string) {
  return doApiGet(`/seller_flows/${leadToken}`).then(handleResponse);
}

export function fetchLead(leadToken: string) {
  return doApiGet(`/seller_leads/${leadToken}`).then(handleResponse);
}

export function cloneLead(leadToken: string) {
  return doApiPost(`/seller_leads/create/from-lead`, { id: leadToken }).then(handleResponse);
}

export function captureInboundCampaignEmail(email: string, campaignName: string) {
  return doApiPost(`/marketing/inbound-campaign-email-capture`, {
    campaign_name: campaignName,
    email,
  }).then(handleHeadResponse);
}

export function fetchBrokerageOffer(brokerageOfferToken: string) {
  return doApiGet(`/brokerage-offers/${brokerageOfferToken}`).then(res => res.json());
}

export function fetchOtherBids(brokerageOfferToken: string) {
  return doApiGet(`/brokerage-offers/${brokerageOfferToken}/other-bids`).then(handleResponse);
}

export function fetchInvitation(invitationToken: string) {
  return doApiGet(`/invites/${invitationToken}`).then(res => res.json());
}

export function moveToInputFlow(
  addressToken: string,
  channel: string,
  trackingKeywords: object = [],
) {
  return doApiPost('/seller_leads/create/verified-address', {
    address_token: addressToken,
    channel,
    tracking_keywords: trackingKeywords,
  }).then(res => res.json());
}

/**
 * @returns {Array.<{ agent_email: String, readable_address: String, slug: String, state: string,}>}
 * @param {string} query - the string you want to match the listing slug against.
 * @param {Object.<string, (string|number|boolean)} [options] - Currently use to set :publicly_visible scope
 */
export function searchListings(query: string, options = {}) {
  const params = queryString.stringify(options);
  return doApiGet(`/listings/search_listings?query=${query}&${params}`).then(res => res.json());
}

export function getListing(slug: string) {
  return doApiGet(`/listings/${slug}`).then(handleResponse);
}

export function getAgent(fullName: string, phone: string) {
  return doApiGet('/customers/find_agent', {
    agent_full_name: fullName,
    agent_phone: phone,
  }).then(handleResponse);
}

/**
 * @returns {Object.<{data: Array<{value: String, label: String}>}>}
 * @example Data can include: { data: [{value: 'cash', label: 'All cash'}, {value: 'fha_loan', label: 'FHA loan'}] }
 */
export async function getFinancingTypes() {
  return doApiGet('/listing_offers/financing_type_options').then(res => res.json());
}

/**
 * @returns {Object.<{data: Array<{value: String, label: String}>}>}
 * @example Data can include: { data: [{value: 'in_contract', label: 'In contract'}] }
 */
export async function getContingentOfferStatuses() {
  return doApiGet('/listing_offers/contingent_offer_status_options').then(res => res.json());
}

export function logIn(email: string, password: string, rememberMe?: boolean) {
  return doPost('/login', { human: { email, password, remember_me: rememberMe } }).then(
    handleResponse,
  );
}

export function trackLead(trackingKeywords: string) {
  return doGet(`/track/${trackingKeywords}`);
}

export function fetchRecentOffer() {
  return createFetch(
    createStack(
      init('credentials', 'include'),
      accept('application/json'),
      header('Content-Type', 'application/json'),
      header('Staging-Integration-Secret', 'X-Consumer-Integration-Test'),
      base(RAILS_URL),
    ),
  )(`/consumer/recent_offer`).then(handleResponse);
}

interface IPostAddressOptions {
  manual?: boolean;
  agent_request?: boolean;
  seller_flow_uuid_override?: string;
  seller_flow_name_override?: string;
  channel?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
}

export function postAddress(
  address: IAddressInputAddress | {},
  {
    manual = false,
    agent_request = false,
    seller_flow_uuid_override,
    seller_flow_name_override,
    channel,
    trackingKeywords,
    partnerReferralId,
  }: IPostAddressOptions = {},
) {
  const method = 'LeadCreate';
  const request = {
    address,
    agent_request,
    channel,
    manual,
    partnerReferralId,
    sellerFlowNameOverride: seller_flow_name_override,
    sellerFlowUUIDOverride: seller_flow_uuid_override,
    state: 'inputting_address',
    trackingKeywords,
  };
  let initialPromise: Promise<any> = Promise.resolve();
  if (trackingKeywords) {
    initialPromise = trackLead(trackingKeywords);
  }
  return initialPromise.then(() => {
    return doBFFPost({ method, request }).then(handleResponse);
  });
}

export function createListingOffer(listingOfferParams: IListingOfferParams) {
  return doApiPost('/listing_offers', listingOfferParams).then(handleResponse);
}

export function getS3DocumentUploadUrl(bucketName: string, documentName: string) {
  return doApiGet('/s3_objects/document_upload_url', {
    bucket_name: bucketName,
    document_name: documentName,
  }).then(handleResponse);
}

export function uploadDocumentToS3(document: File, s3UploadUrl: string) {
  return doFileUpload(s3UploadUrl, document).then(res => {
    // we're handling the response inline because AWS returns an empty response,
    // which causes handleResponse to throw an exception
    if (res.ok) {
      return {};
    }
    throw new Error(`${res.status} ${res.statusText}`);
  });
}

export function acceptInvite(invitationToken: string, email: string, password?: string) {
  return doApiPost(`/invites/${invitationToken}/accept`, { email, password }).then(handleResponse);
}

export function saveBrokerageOfferFeedback(brokerageOfferToken, declineReasons, declineFeedback) {
  return doApiPost(`/brokerage-offers/${brokerageOfferToken}/save-feedback`, {
    decline_feedback: declineFeedback,
    decline_reasons: declineReasons,
  }).then(handleResponse);
}

export function requestNewOffer(brokerageOfferToken: string) {
  return doApiPost(`/brokerage-offers/${brokerageOfferToken}/request-new-offer`, {}).then(
    handleResponse,
  );
}

export function fetchSellerReturnExperience(leadToken: string) {
  const method = 'FetchReturnExperience';
  const request = leadToken;
  return doBFFPost({ method, request }).then(handleResponse);
}

export function submitPurchaseAgreement(brokerageOfferToken, purchaseAgreement) {
  const { closeDate, hasHoa, hoaFees, hoaName, signerNames } = purchaseAgreement;

  const purchaseAgreementPayload = {
    close_of_escrow_date: closeDate.valueOf(),
    hoa_fees: hasHoa
      ? hoaFees.map(hoaFee => ({
          amount: hoaFee.hoaFeeCents,
          frequency: hoaFee.hoaFeeFrequency,
        }))
      : [],
    hoa_name: hasHoa ? hoaName : null,
    signer_names: signerNames,
  };

  return doApiPost(
    `/brokerage-offers/${brokerageOfferToken}/purchase-agreement`,
    purchaseAgreementPayload,
  ).then(handleResponse);
}

export function reportError(postData) {
  return doApiPost(`/brokerage-offers/${postData.brokerageOfferToken}/report-error`, postData).then(
    res => res.json(),
  );
}
